import { useEffect, useRef, useState } from "react";

/**
 * VideoTile, autoplaying muted video loop with image poster fallback.
 *
 * IntersectionObserver lazy-mounts the <video> only when the tile is
 * (or about to be) in view. `key={video}` ensures the DOM is rebuilt
 * whenever the URL changes, defeating browser cache and bypassing the
 * stale-source bug where browsers keep an old buffered MP4.
 */
export default function VideoTile({
  video,
  poster,
  alt = "",
  overlay = true,
  className = "",
}) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !video) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setInView(true);
        });
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [video]);

  // Some browsers (Safari) ignore the autoplay attribute when src is set
  // after mount, call play() explicitly once the video is in view.
  useEffect(() => {
    if (!inView) return;
    const v = videoRef.current;
    if (!v || typeof v.play !== "function") return;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [inView, video]);

  return (
    <div className={`video-tile ${className}`} ref={wrapRef}>
      {video && inView ? (
        <video
          ref={videoRef}
          key={video}
          src={video}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
      ) : (
        <img className="poster" src={poster} alt={alt} loading="lazy" />
      )}
      {overlay && <div className="tile-overlay" aria-hidden="true" />}
    </div>
  );
}
